import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/common/Icon';
import { ReactComponent as CopyIcon } from '../../../../icons/copy.svg';
import { ReactComponent as CheckIcon } from '../../../../icons/check.svg';
import { ReactComponent as ShareIcon } from '../../../../icons/share-ios.svg';
import { ReactComponent as QrIcon } from '../../../../icons/qr-code.svg';
import { allowJoining } from '../../../../services/teamService';
import InviteQrModal from '../InviteQrModal';

import './TeamInvitation.scss';

// Phones get the native share sheet; desktops without one fall back to copying
const canShare = typeof navigator !== 'undefined' && !!navigator.share;

const TeamInvitation = ({ team, inviteLink, teamId, onCopyText }) => {
  const { t } = useTranslation();
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [joiningAllowed, setJoiningAllowed] = useState(!!team.allowJoining);

  const handleAllowJoining = async () => {
    const next = !joiningAllowed;

    setJoiningAllowed(next);

    try {
      await allowJoining(next);
    } catch (ex) {
      setJoiningAllowed(!next);
      toast.error(t('team.invitation.saveFailed'));
    }
  };

  const share = async (data) => {
    try {
      await navigator.share(data);
    } catch (error) {
      // AbortError is the person closing the sheet
      if (error.name !== 'AbortError') toast.error(t('team.invitation.shareFailed'));
    }
  };

  const handleCopyLink = async () => {
    if (canShare) return share({ title: t('team.invitation.shareTitle', { team: team.name }), url: inviteLink });

    const success = await onCopyText(inviteLink);

    if (success) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleCopyId = async () => {
    if (canShare) return share({ text: teamId });

    const success = await onCopyText(teamId);

    if (success) {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const renderCopyButton = (copied, onClick, label) => (
    <button type="button" className={`copy ${copied ? 'copy--done' : ''}`} onClick={onClick} aria-label={label}>
      <Icon icon={copied ? <CheckIcon /> : canShare ? <ShareIcon /> : <CopyIcon />} />
      {copied ? t('team.invitation.copied') : canShare ? t('common.share') : t('team.invitation.copy')}
    </button>
  );

  return (
    <div className="TeamInvitation">
      <div className="row">
        <div className="text">
          <p className="label">{t('team.invitation.allowNew')}</p>
          <p className="description">{t('team.invitation.allowNewHint')}</p>
        </div>
        <label className="switch with-text">
          <input type="checkbox" name="allowJoining" checked={joiningAllowed} onChange={handleAllowJoining} />
          <span className="slider round">
            <span className="slider-text">{joiningAllowed ? t('common.on') : t('common.off')}</span>
          </span>
        </label>
      </div>

      <div className="row">
        <div className="text">
          <p className="label">{t('team.invitation.link')}</p>
          <p className="description">{t('team.invitation.linkHint', { team: team.name })}</p>
        </div>
        <div className="buttons">
          <button type="button" className="qr" onClick={() => setShowQr(true)} aria-label={t('team.invitation.showQr')}>
            <Icon icon={<QrIcon />} />
          </button>
          {renderCopyButton(
            copiedLink,
            handleCopyLink,
            canShare ? t('team.invitation.shareLink') : t('team.invitation.copyLink'),
          )}
        </div>
      </div>

      <div className="row">
        <div className="text">
          <p className="label">{t('team.invitation.teamId')}</p>
          <p className="team-id">{teamId}</p>
        </div>
        {renderCopyButton(
          copiedId,
          handleCopyId,
          canShare ? t('team.invitation.shareId') : t('team.invitation.copyId'),
        )}
      </div>

      <InviteQrModal isOpen={showQr} onClose={() => setShowQr(false)} inviteLink={inviteLink} teamName={team.name} />
    </div>
  );
};

export default TeamInvitation;
